import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { IUsersPathParams, IUserRolesBody, IUserReportsBody, IUserCompaniesBody } from '../../shared/models/user.model';
import {
  IUserAccountsQueryParams,
  ICreateUserAccount,
  IUserAccountsCountQueryParams,
  IUpdateUserAccount
} from '../../shared/models/user-accounts.model';
import { ErrorHandlerService } from './error-handler.service';
import { CompanyService } from './company.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly baseUrl = environment.baseBackendUrl;

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    private companyService: CompanyService
  ) { }

  /**
   * Asigna roles a un usuario
   * @param userId ID del usuario
   * @param rolesData Datos de los roles a asignar
   */
  assignUserRoles(userId: number, rolesData: IUserRolesBody): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/api/admin/users/${userId}/roles`, rolesData, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Elimina un UserAccount por ID (formato OData)
   * @param key ID del UserAccount a eliminar
   */
  deleteUserAccountByKey(key: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/odata/UserAccounts(${key})`, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Actualiza parcialmente un UserAccount por ID (PATCH OData)
   * @param key ID del UserAccount a actualizar
   * @param data Datos a actualizar
   */
  patchUserAccountByKey(key: number, data: IUpdateUserAccount): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/odata/UserAccounts(${key})`, data, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Crea o reemplaza una relación específica de un UserAccount (PUT $ref)
   * @param key ID del UserAccount
   * @param navigationProperty Nombre de la relación (por defecto 'Companies')
   * @param relatedKey ID de la entidad relacionada
   */
  putUserAccountRelationRef(key: number, navigationProperty: string = 'Companies', relatedKey: number): Observable<any> {
    const url = `${this.baseUrl}/odata/UserAccounts/${key}/${navigationProperty}/${relatedKey}/$ref`;
    return this.http.put<any>(url, {}, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Elimina una relación específica de un UserAccount (DELETE $ref)
   * @param key ID del UserAccount
   * @param navigationProperty Nombre de la relación (por defecto 'Companies')
   * @param relatedKey ID de la entidad relacionada
   */
  deleteUserAccountRelationRef(key: number, navigationProperty: string = 'Companies', relatedKey: number): Observable<any> {
    const url = `${this.baseUrl}/odata/UserAccounts/${key}/${navigationProperty}/${relatedKey}/$ref`;
    return this.http.delete<any>(url, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Promueve un usuario
   * @param userId ID del usuario a promover
   */
  promoteUser(userId: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/api/admin/users/${userId}/promote`, {}, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Asigna reports a un usuario
   * @param userId ID del usuario
   * @param reportsData Datos de los reports a asignar
   */
  assignUserReports(userId: number, reportsData: IUserReportsBody): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/api/admin/users/${userId}/reports`, reportsData, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Asigna companies a un usuario
   * @param userId ID del usuario
   * @param companiesData Datos de las companies a asignar
   */
  assignUserCompanies(userId: number, companiesData: IUserCompaniesBody): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/api/admin/users/${userId}/companies`, companiesData, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene todas las cuentas de usuario con filtros opcionales
   * @param params Parámetros de consulta OData
   */
  getUserAccounts(params?: IUserAccountsQueryParams): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/odata/UserAccounts`, {
      params: this.buildQueryParams(params),
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }


  /**
   * Obtiene la cuenta de usuario
   * @param id Id del usuario de interes
   * @param params Parámetros de consulta OData
   */
  getUserAccount(id: number, params?: IUserAccountsQueryParams): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/odata/UserAccounts(${id})`, {
      params: this.buildQueryParams(params),
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Crea un nuevo usuario (flujo de creación)
   * Espera payload con estructura: { userName, email, name, surName }
   */
  createUserAccount(userAccountData: ICreateUserAccount): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/odata/UserAccounts`, userAccountData, {
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene el conteo las cuentas de usuario con filtros opcionales
   * @param params Parámetros de consulta OData
   */
  getUserAccountsCount(params?: IUserAccountsCountQueryParams): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/odata/UserAccounts/$count`, {
      params: this.buildQueryParams(params),
      headers: this.buildHeaders()
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Construye los headers HTTP incluyendo el X-Company-Id
   */
  private buildHeaders(): HttpHeaders {
    const selectedCompany = this.companyService.selectedCompany$.value;
    let headers = new HttpHeaders();

    if (selectedCompany?.id) {
      headers = headers.set('X-Company-Id', selectedCompany.id.toString());
    }

    return headers;
  }

  /**
   * Construye los parámetros de consulta HTTP
   * @param params Parámetros de consulta
   */
  private buildQueryParams(params: any): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key].toString());
        }
      });
    }
    return httpParams;
  }
}
